import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme } from 'styled-components'
import { getPaletteColor } from 'pcln-design-system'
import Range, { Slider } from 'pcln-slider'
import css from './rc-slider-css'
import { markedValuesPropType } from '../../../helpers/commonPropTypes'

const propTypes = {
  ariaLabelForHandle: PropTypes.string,
  ariaLabelGroupForHandles: PropTypes.arrayOf(PropTypes.string),
  ariaLabelledByForHandle: PropTypes.string,
  ariaLabelledByGroupForHandles: PropTypes.arrayOf(PropTypes.string),
  ariaValueTextFormatterForHandle: PropTypes.func,
  ariaValueTextFormattersForHandles: PropTypes.arrayOf(PropTypes.func),
  className: PropTypes.string,
  defaultValueArray: PropTypes.arrayOf(PropTypes.number), // pass in one number for oneWay, two for twoWay min and max
  disabled: PropTypes.bool,
  handleAfterChange: PropTypes.func,
  handleChange: PropTypes.func,
  markedValues: markedValuesPropType,
  max: PropTypes.number,
  min: PropTypes.number,
  oneWay: PropTypes.bool,
  step: PropTypes.number,
  theme: PropTypes.shape({
    colors: PropTypes.object.isRequired,
  }).isRequired,
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
}

const defaultProps = {
  disabled: false,
  oneWay: false,
  step: 1,
}

const RangeSliderWrap = styled.div`
  padding: 0 9px;

  ${css}

  .rc-slider-disabled {
    background-color: transparent;
  }
`

const SliderComponent = ({
  oneWay,
  handleChange,
  handleAfterChange,
  defaultValueArray,
  value,
  min,
  markedValues,
  max,
  disabled,
  step,
  theme,
  ariaLabelGroupForHandles,
  ariaLabelledByGroupForHandles,
  ariaValueTextFormattersForHandles,
  ariaLabelForHandle,
  ariaLabelledByForHandle,
  ariaValueTextFormatterForHandle,
}) => {
  const [marks, setMarks] = useState({})
  useEffect(() => {
    const newMarks = markedValues?.marks?.reduce((marks, mark) => {
      marks[mark.value] = {
        style: {
          pointerEvents: 'none',
          top: '-8px',
        },
        label: mark.label,
      }
      return marks
    }, {})
    setMarks(newMarks)
  }, [markedValues])

  const rangeStyle = {
    padding: 0,
    marginTop: 16,
  }

  const railStyle = {
    backgroundColor: `${getPaletteColor('background.light')({ theme })}`,
    height: 4,
  }

  const selectedRangeStyle = {
    backgroundColor: `${getPaletteColor(disabled ? 'background.dark' : 'primary.base')({ theme })}`,
    height: 4,
  }

  const handleStyle = {
    backgroundColor: `${getPaletteColor('background.lightest')({ theme })}`,
    border: `solid 2px ${getPaletteColor(disabled ? 'border.light' : 'primary.base')({ theme })}`,
    width: 20,
    height: 20,
    marginLeft: -10,
    marginTop: -8,
    outline: 'none',
  }

  return oneWay ? (
    <Slider
      style={rangeStyle}
      railStyle={railStyle}
      trackStyle={selectedRangeStyle}
      handleStyle={handleStyle}
      defaultValue={defaultValueArray ? defaultValueArray[0] : min}
      value={value ? value[0] : undefined}
      min={min}
      max={max}
      marks={marks}
      onChange={handleChange}
      onAfterChange={handleAfterChange}
      disabled={disabled}
      step={step}
      ariaLabelForHandle={ariaLabelForHandle}
      ariaLabelledByForHandle={ariaLabelledByForHandle}
      ariaValueTextFormatterForHandle={ariaValueTextFormatterForHandle}
    />
  ) : (
    <Range
      style={rangeStyle}
      railStyle={railStyle}
      trackStyle={[selectedRangeStyle, selectedRangeStyle]}
      handleStyle={[handleStyle, handleStyle]}
      defaultValue={defaultValueArray || [min, max]}
      value={value}
      min={min}
      max={max}
      marks={marks}
      allowCross={false}
      onChange={handleChange}
      onAfterChange={handleAfterChange}
      disabled={disabled}
      step={step}
      ariaLabelGroupForHandles={ariaLabelGroupForHandles}
      ariaLabelledByGroupForHandles={ariaLabelledByGroupForHandles}
      ariaValueTextFormattersForHandles={ariaValueTextFormattersForHandles}
    />
  )
}

const RangeSlider = ({
  className,
  oneWay,
  handleChange,
  handleAfterChange,
  defaultValueArray,
  value,
  min,
  markedValues,
  max,
  disabled,
  step,
  theme,
  ariaLabelGroupForHandles,
  ariaLabelledByGroupForHandles,
  ariaValueTextFormattersForHandles,
  ariaLabelForHandle,
  ariaLabelledByForHandle,
  ariaValueTextFormatterForHandle,
}) => {
  return (
    <RangeSliderWrap className={className} disabled={disabled} aria-label='Slider'>
      <SliderComponent
        oneWay={oneWay}
        handleChange={handleChange}
        handleAfterChange={handleAfterChange}
        defaultValueArray={defaultValueArray}
        value={value}
        min={min}
        max={max}
        markedValues={markedValues}
        disabled={disabled}
        step={step}
        theme={theme}
        ariaLabelGroupForHandles={ariaLabelGroupForHandles}
        ariaLabelledByGroupForHandles={ariaLabelledByGroupForHandles}
        ariaValueTextFormattersForHandles={ariaValueTextFormattersForHandles}
        ariaLabelForHandle={ariaLabelForHandle}
        ariaLabelledByForHandle={ariaLabelledByForHandle}
        ariaValueTextFormatterForHandle={ariaValueTextFormatterForHandle}
      />
    </RangeSliderWrap>
  )
}

SliderComponent.propTypes = propTypes

RangeSlider.propTypes = propTypes
RangeSlider.defaultProps = defaultProps

export default withTheme(RangeSlider)
